import React from "react";
import styled from "styled-components";

import ItemGroup from "./ItemGroup";
import { IRouteItem, settings } from "src/components/FinaLayout/routes";
import { useClient } from "src/store/hooks";
import { Avatar } from "antd";
import { UserIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { StyledUsername, StyledUsernameText } from "./FinaSidebar.style";
import CollapseButton from "./CollapseButton";

const Group = styled.div<{ $collapse?: boolean; scroll?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  /* Collapsed, the rows are icons with their labels hidden, so aligning them to
     the left of the rail put them 12px off the logo above and the avatar below,
     both of which are centred. There is nothing left to left-align. */
  align-items: ${({ $collapse }) => ($collapse ? "center" : "stretch")};
  flex-wrap: nowrap;
  /* The rail's own gutter — what insets a row's tinted background from the
     edge, and what the active marker is measured back to. Only the scrolling
     list carries it: on the wrapper as well it doubled, pushing every row
     24px in and leaving the marker floating inside the rail. */
  padding: ${({ scroll, $collapse }) =>
    scroll ? `4px ${$collapse ? "8px" : "12px"} 12px` : "0"};
  /* A flex child defaults to min-height:auto, so flex-grow alone cannot
     shrink it below its content: the nav grows to its full height and pushes
     the rail past the viewport instead of scrolling inside it. */
  min-height: 0;
  overflow-y: ${({ scroll }) => (scroll ? "auto" : "hidden")};
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

type Props = {
  routes?: IRouteItem;
  collapse: boolean;
  /** Omitted on mobile, where the rail is opened by the header instead. */
  setCollapse?: (collapse: boolean) => void;
};

const Menu: React.FC<Props> = ({ routes, collapse, setCollapse }) => {
  const { data } = useClient();  
  return (
    <>
      <Group $collapse={collapse}>
        <Group scroll $collapse={collapse}>
          {Object.keys(routes).map((route, index) => (
            <ItemGroup
              collapse={collapse}
              key={route}
              items={routes[route]}
              divider={index > 0}
            />
          ))}
          <ItemGroup collapse={collapse} items={settings} divider />
        </Group>
      </Group>
      <StyledUsername $collapse={collapse}>
        <Avatar
          size="default"
          style={{ cursor: "pointer" }}
          icon={<HugeIcon icon={UserIcon} />}
          src={data?.picture}
          alt="Avatar"
        />
        {!collapse && (
          <StyledUsernameText>
            {data?.name?.first} {data?.name?.last}
          </StyledUsernameText>
        )}
        {/* Right of the name when the rail is open, and under the avatar when
            it is not — 56px has no room for two things side by side, and the
            control has to be reachable in both states or the rail cannot be
            reopened. */}
        {setCollapse && (
          <CollapseButton collapse={collapse} setCollapse={setCollapse} />
        )}
      </StyledUsername>
    </>
  );
};

export default Menu;
